'use client';

import { cn } from '@/lib/utils';

import NextImage from '@/components/atoms/image';
import {
  ImageSBProps,
  transformCMSAssetToNextImageProps,
} from '@/components/atoms/image/ImageSB';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import { Text } from '@/components/atoms/text';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/toggle/useCMSToggle';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import { SimpleCards, SimpleCardsProps } from '@/features/cards/SimpleCards';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContent';
import { BlockWrapper } from '@/features/utils/BlockWrapper';

export interface SimpleCardsStrapiProps {
  header?: BlockHeader;
  alignCardsContent: SimpleCardsProps['align'];
  cardsVariant: 'large' | 'small';
  layout: 'one-column' | 'two-columns' | 'two-columns-reverse';
  belowText?: RichTextFieldSBProps;
  cards: {
    top?: ImageSBProps;
    title?: string;
    description?: RichTextFieldSBProps;
    url?: string;
  }[];
  toggle?: ToggleCMSProps;
}

export default function SimpleCardsStrapi({
  block,
  ...rest
}: BlockComponentModel<SimpleCardsStrapiProps>) {
  const {
    cards,
    alignCardsContent = 'center',
    header,
    cardsVariant = 'small',
    layout = 'one-column',
    belowText,
    toggle,
  } = block;
  const { filteredToggleList, toggleNode: ToggleNode } = useCMSToggle<
    typeof cards
  >(cards, toggle);

  const variantsClasses = {
    large: {
      title: 'text-neutral-N80 text-subtitle-l',
      description: 'mt-6',
    },
    small: {
      title: 'text-wrap',
      description: 'mt-4 text-subtitle-l',
    },
  } as const;

  const layoutGapClasses =
    cardsVariant === 'small' ? 'md:gap-x-10' : 'md:gap-x-6';
  const layoutClasses = {
    'one-column': 'md:flex-col',
    'two-columns': cn('md:flex-row md:items-start', layoutGapClasses),
    'two-columns-reverse': cn(
      'md:flex-row-reverse md:items-start',
      layoutGapClasses
    ),
  } as const;

  const topChildrenToggle =
    layout === 'one-column' && ToggleNode && ToggleNode();

  const topChildrenBelowText = layout !== 'one-column' && belowText && (
    <div className='text-neutral-N80' data-tracker-component='additional-info'>
      <RichTextFieldSB block={belowText} />
    </div>
  );

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section={SECTION_NAMES.SIMPLE_CARDS}>
        <BlockHeaderContent
          {...header}
          classNames={{
            root: cn(
              layoutClasses?.[layout] || layoutClasses['one-column'],
              'self-stretch'
            ),
            header: cn(
              layout !== 'one-column' &&
                'md:mb-0 lg:mb-0 md:items-start md:text-left'
            ),
            button: cn(layout !== 'one-column' && 'md:justify-start'),
          }}
          buttonPosition={
            layout !== 'one-column' ? 'top' : header?.buttonPosition
          }
          bottomChildren={
            layout === 'one-column' && belowText ? (
              <div
                className='text-neutral-N80 mt-8 md:mt-9'
                data-tracker-component='additional-info'
              >
                <RichTextFieldSB block={belowText} />
              </div>
            ) : null
          }
          topChildren={topChildrenToggle || topChildrenBelowText}
        >
          {cards.map((card, index) => (
            <div
              key={card.title}
              className={cn(
                'self-stretch',
                filteredToggleList?.length &&
                  !filteredToggleList?.some(
                    (item) => card.title === item.title
                  ) &&
                  'hide-content'
              )}
            >
              <SimpleCards
                key={index}
                align={alignCardsContent}
                width={cardsVariant}
                url={card?.url}
                classNames={{ root: 'h-full' }}
              >
                {card.top && (
                  <NextImage
                    {...transformCMSAssetToNextImageProps(card.top)}
                    classNames={{
                      container: 'mb-4',
                      image: cn(cardsVariant === 'large' && 'w-9'),
                    }}
                  />
                )}
                {card.title && (
                  <Text
                    classNames={{
                      wrapper: variantsClasses[cardsVariant].title,
                    }}
                  >
                    {card.title}
                  </Text>
                )}
                {!!card.description?.text?.length && (
                  <div className={variantsClasses[cardsVariant].description}>
                    <RichTextFieldSB block={card.description} />
                  </div>
                )}
              </SimpleCards>
            </div>
          ))}
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
